import React, { useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ArrowRight } from "lucide-react";
import Nav from "./Nav";
import Footer from "./Footer";
import BlogCover from "./BlogCover";
import Logo from "./Logo";
import { POSTS, getPost } from "./posts";
import { BODIES } from "./postBodies";
import { Toaster } from "sonner";

function Block({ block }) {
  if (block.type === "h2") {
    return (
      <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tightest leading-[1.05] mt-14 mb-5">
        {block.text}
      </h2>
    );
  }
  if (block.type === "p") {
    return <p className="text-lg md:text-xl leading-[1.7] text-ink2 my-5">{block.text}</p>;
  }
  if (block.type === "ul") {
    return (
      <ul className="my-6 space-y-3">
        {block.items.map((it, i) => (
          <li
            key={i}
            className="relative pl-8 text-lg leading-[1.6] text-ink2 before:content-[''] before:absolute before:left-0 before:top-[0.7em] before:w-4 before:h-[2px] before:bg-signal"
          >
            {it}
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "callout") {
    return (
      <div className="my-10 border-l-4 border-signal pl-6 md:pl-8 py-2">
        <p className="font-heading font-bold text-xl md:text-2xl leading-[1.3] tracking-tight text-ink">
          "{block.text}"
        </p>
      </div>
    );
  }
  if (block.type === "quote") {
    return (
      <blockquote className="my-10 border border-ink p-6 md:p-8">
        <p className="font-heading font-bold text-xl md:text-2xl leading-[1.3] tracking-tight">
          {block.text}
        </p>
      </blockquote>
    );
  }
  return null;
}

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPost(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const idx = useMemo(() => POSTS.findIndex((p) => p.slug === slug), [slug]);
  const prev = idx > 0 ? POSTS[idx - 1] : null;
  const next = idx >= 0 && idx < POSTS.length - 1 ? POSTS[idx + 1] : null;
  const body = post ? BODIES[post.slug] : null;

  if (!post) {
    return (
      <div className="min-h-screen bg-paper text-ink">
        <Nav />
        <main className="pt-[160px] pb-40 max-w-3xl mx-auto px-6 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal mb-6">
            ◆ Not found
          </p>
          <h1 className="font-heading font-black text-5xl md:text-7xl leading-[0.95] tracking-tightest mb-8">
            This essay wandered off.
          </h1>
          <button
            onClick={() => navigate("/blog")}
            className="inline-flex items-center gap-2 bg-ink text-paper hover:bg-signal px-6 py-4 font-heading font-bold text-[12px] uppercase tracking-[0.2em] transition-colors"
            data-testid="article-not-found-back"
          >
            <ArrowLeft size={14} /> Back to journal
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid={`article-page-${post.slug}`}>
      <Nav />

      <article className="pt-[120px] md:pt-[160px]">
        {/* Breadcrumb / status */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between border-t border-b border-line py-3 mb-10 md:mb-14 font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">
            <Link
              to="/blog"
              data-testid="article-back-journal"
              className="inline-flex items-center gap-2 hover:text-signal transition-colors"
            >
              <ArrowLeft size={12} /> Journal
            </Link>
            <span>{post.categories.join(" · ")}</span>
            <span className="hidden md:inline">{post.date} · {post.readTime}</span>
          </div>
        </div>

        {/* Masthead */}
        <header className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-end pb-14 md:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-8"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal mb-6">
              ◆ Essay · §{String(idx + 1).padStart(2, "0")}
            </p>
            <h1
              data-testid="article-heading"
              className="font-heading font-black text-[40px] sm:text-[64px] md:text-[84px] leading-[0.92] tracking-tightest"
            >
              {post.title}
            </h1>
            <p className="mt-6 md:mt-8 text-lg md:text-2xl text-ink2 leading-[1.5] max-w-3xl">
              {post.excerpt}
            </p>
            <div className="mt-10 flex items-center gap-4 flex-wrap">
              <Logo variant="dark" size={40} />
              <div className="leading-tight">
                <div className="font-heading font-bold text-base">Abhinav Dhiman</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2 mt-1">
                  The Digital Marketer · {post.date}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="md:col-span-4 aspect-[3/4] border border-ink overflow-hidden"
          >
            <BlogCover post={{ ...post, index: idx }} />
          </motion.div>
        </header>

        {/* Body */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
          {body ? (
            body.map((b, i) => <Block key={i} block={b} />)
          ) : (
            <p className="text-lg text-ink2">Content coming soon.</p>
          )}
        </div>

        {/* End-of-article CTA */}
        <section className="bg-ink text-paper py-16 md:py-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal mb-4">
                ◆ Put the ideas to work
              </div>
              <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tightest">
                Liked the thinking? <br />
                <span className="italic font-medium">Let's apply it to your funnel.</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:justify-self-end">
              <Link
                to="/#contact"
                data-testid="article-cta-contact"
                className="inline-flex items-center gap-3 bg-signal text-paper hover:bg-paper hover:text-ink px-8 py-5 font-heading font-bold text-[13px] uppercase tracking-[0.2em] transition-colors"
              >
                Book a Call <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </section>

        {/* Prev / Next */}
        <nav className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-2 border-t border-line">
          {prev ? (
            <Link
              to={`/blog/${prev.slug}`}
              data-testid="article-prev"
              className="group p-6 md:p-8 border-r-0 md:border-r border-line hover:bg-surface transition-colors"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2 inline-flex items-center gap-2">
                <ArrowLeft size={12} /> Previous essay
              </span>
              <div className="mt-3 font-heading font-bold text-2xl md:text-3xl leading-tight tracking-tight group-hover:text-signal transition-colors">
                {prev.title}
              </div>
            </Link>
          ) : (
            <div className="p-6 md:p-8 border-r-0 md:border-r border-line" />
          )}
          {next ? (
            <Link
              to={`/blog/${next.slug}`}
              data-testid="article-next"
              className="group p-6 md:p-8 hover:bg-surface transition-colors md:text-right"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2 inline-flex items-center gap-2 md:flex-row-reverse">
                Next essay <ArrowRight size={12} />
              </span>
              <div className="mt-3 font-heading font-bold text-2xl md:text-3xl leading-tight tracking-tight group-hover:text-signal transition-colors">
                {next.title}
              </div>
            </Link>
          ) : (
            <div className="p-6 md:p-8" />
          )}
        </nav>
      </article>

      <Footer />
      <Toaster
        position="bottom-right"
        offset={104}
        toastOptions={{
          style: {
            background: "#0A0A0A",
            color: "#FDFDFD",
            border: "1px solid #2A2A2A",
            borderRadius: 0,
            fontFamily: "Satoshi, sans-serif",
          },
        }}
      />
    </div>
  );
}
